import { useEffect, useState } from "react";

import { useUser } from "@/src/context/user-context";
import { useDebounce } from "@/src/hooks/use-debounce";
import { autocompletePlaces, fetchPlaceDetails } from "@/src/lib/geocoding-client";
import { buildLocation } from "@/src/lib/geocoding-utils";
import { logger } from "@/src/lib/logging/logger";
import { getOperatingStatesForGroup } from "@/src/models/group";
import { Location } from "@/src/models/location";

export const useGeocodingSearch = (searchTerm: string) => {
  const { group } = useUser();
  const groupStates = group ? getOperatingStatesForGroup(group) : [];
  const stateCode = groupStates?.length === 1 ? groupStates[0] : undefined;

  const [results, setResults] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  const buildLocationFromGeocode = async (geocodeInfo: any, matchedLocation: Location | null): Promise<Location> => {
    const placeId = geocodeInfo.place_id;

    try {
      const placeDetails = await fetchPlaceDetails(placeId);

      return buildLocation({
        placeDetails,
        geocodeInfo,
        components: placeDetails,
        matchedLocation,
        placeId,
      });
    } catch (error) {
      logger.error("Error building location from geocode:", { error });

      return buildLocation({
        geocodeInfo,
        matchedLocation,
        placeId,
      });
    }
  };

  useEffect(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.length < 2) {
      setResults([]);

      setError(null);

      return;
    }

    setLoading(true);

    setError(null);

    const searchPlaces = async () => {
      try {
        const geocodeResults = await autocompletePlaces({
          input: debouncedSearchTerm,
          maxResults: 5,
          stateCode,
        });

        // Convert autocomplete results to Location objects
        // TODO: Opportunity to save on N fetchPlaceDetails calls per keystroke by using
        // autocomplete results as labels (tradeoff of autocomplete results not having full address)
        const locationPromises = geocodeResults.map(async (result) => {
          return await buildLocationFromGeocode(result, null);
        });

        const locations = await Promise.all(locationPromises);

        setResults(locations);
      } catch (err) {
        logger.error("Error building location from geocode:", { error: err });

        setError(err instanceof Error ? err.message : "Failed to search places");

        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    searchPlaces();
  }, [debouncedSearchTerm, stateCode]);

  return { results, loading, error };
};
